import { jsx as _jsx } from "react/jsx-runtime";
import { SpecraBadge, SpecraEyebrow, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraPageHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { useLaborCategoryHistory, useLaborCategoryMappings, useRateObservations } from '../../Delivery/Registries/hooks';
import { mappingVariant } from '../badgeVariants';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { registryHref } from '../registryHref';
import { LaborCategoryEntryDetail } from './lcat/LaborCategoryEntryDetail';
/**
 * Labor category entry page, REG-19: today's drawer as a page. Route
 * /registries/labor-categories/entries/:entryId behind registries_records_v1.
 */ export function LaborCategoryEntryPage() {
    const { entryId = '' } = useParams();
    const { user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    const history = useLaborCategoryHistory(entryId, tenantId);
    const entry = history.data?.find((e)=>e.id === entryId);
    const mappings = useLaborCategoryMappings(tenantId, {
        entryId
    }, {
        enabled: !!entry
    });
    const rates = useRateObservations(tenantId, {
        lcatEntryId: entryId,
        acrossVersions: true,
        includeCorrected: true
    }, {
        enabled: !!entry
    });
    const status = history.isPending ? 'loading' : history.isError ? 'error' : entry ? 'ready' : 'unavailable';
    const anchors = (mappings.data ?? []).filter((m)=>m.toAnchorId);
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Labor Categories",
        tab: "lcats",
        title: entry?.title,
        status: status,
        error: history.error,
        onRetry: ()=>void history.refetch(),
        header: entry && /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            eyebrow: /*#__PURE__*/ _jsx(SpecraEyebrow, {
                children: "Labor category · as written"
            }),
            title: entry.title,
            description: [
                entry.companyName,
                entry.contractVehicleName,
                `v${entry.version}`
            ].filter(Boolean).join(' · ')
        }),
        railSections: entry ? [
            {
                id: 'mapped-to',
                title: 'Mapped to',
                rows: anchors.map((m)=>({
                        id: m.id,
                        label: /*#__PURE__*/ _jsx(SpecraLink, {
                            href: registryHref({
                                kind: 'LCAT_ANCHOR',
                                id: m.toAnchorId
                            }) ?? undefined,
                            renderLink: renderRouterLink,
                            children: m.toAnchorTitle
                        }),
                        value: /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: mappingVariant(m.status, m.mappingType),
                            children: m.status === 'APPROVED' ? m.mappingType.toLowerCase() : 'needs review'
                        })
                    })),
                // An empty rail section read as a broken panel; say what the state is.
                children: anchors.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    "data-testid": Testids.registryLcatAnchor.entryNotMapped,
                    children: "Not mapped to an occupational anchor yet."
                }) : undefined
            }
        ] : undefined,
        children: entry && /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.registryLcatAnchor.entryPage,
            children: /*#__PURE__*/ _jsx(LaborCategoryEntryDetail, {
                entry: entry,
                versions: history.data ?? [],
                mappings: mappings.data ?? [],
                rates: rates.data ?? []
            })
        })
    });
}
